import React, { useState, createContext, useEffect, useCallback } from "react";
import { useApolloClient } from "@apollo/react-hooks";
import queries from "../helpers/queries";
import {
  clearStoredAuth,
  isStoredAuthSessionValid,
} from "../helpers/authSession";

export const UserContext = createContext({
  auth: {
    token: null,
    name: null,
    userId: null,
    type: null,
    loaded: null,
    ic: null,
    customRank: null,
    image: null,
    cp58: null,
    newcp58: null,
    login: () => {},
    logout: () => {},
    fetchUser: () => {},
  },
});

export const UserProvider = ({ children }) => {
  const apollo = useApolloClient();

  const login = (authData, rememberMe) => {
    const authWithTimestamp = {
        ...authData,
        timestamp: Date.now(), // Add timestamp when user logs in
    };

    setAuth({
        ...auth,
        ...authWithTimestamp,
        loaded: true,
    });

    if (rememberMe) {
        localStorage.setItem("auth", JSON.stringify(authWithTimestamp));
    } else {
        sessionStorage.setItem("auth", JSON.stringify(authWithTimestamp));
    }
};


  const logout = () => {
    setAuth({
      ...auth,
      token: null,
      name: null,
      userId: null,
      type: null,
      loaded: true,
      ic: null,
      customRank: null,
      image: null,
      cp58: null,
      newcp58: null,
    });

    sessionStorage.clear();
    localStorage.clear();
  };
  

  const fetchUser = useCallback(async () => {
    const existingItem =
      JSON.parse(sessionStorage.getItem("auth")) ||
      JSON.parse(localStorage.getItem("auth"));

    if (existingItem) {
      if (!isStoredAuthSessionValid(existingItem)) {
        clearStoredAuth();
        setAuth((auth) => ({
          ...auth,
          token: null,
          name: null,
          userId: null,
          type: null,
          loaded: true,
          ic: null,
          customRank: null,
          image: null,
          cp58: null,
          newcp58: null,
        }));
        return;
      }

      (async () => {
        const payload = await apollo.query({
          query: queries.WHOAMI,
        });

        if (payload.data.whoami.passwordChanged) {
          localStorage.removeItem("auth");
          sessionStorage.removeItem("auth");
          window.location.href = "/login"; 
          return;
        } 

        setAuth((auth) => ({
          ...auth,
          ...existingItem,
          ...payload.data.whoami,
          loaded: true,
        }));

      
      })();
    } else {
      setAuth((auth) => ({
        ...auth,
        loaded: true,
      }));
    }
  }, [apollo]);

  const [auth, setAuth] = useState({
    token: null,
    name: null,
    userId: null,
    type: null,
    loaded: false,
    ic: null,
    customRank: null,
    image: null,
    cp58: null,
    newcp58: null,
    login,
    logout,
    fetchUser,
  });

  useEffect(() => {
    fetchUser();
  }, [apollo, fetchUser]);

  return (
    <UserContext.Provider value={{ auth }}>{children}</UserContext.Provider>
  );
};

export const UserConsumer = UserContext.Consumer;

export const withUser = (Component) => (props) => {
  return (
    <UserConsumer>
      {(user) => <Component {...props} user={user} />}
    </UserConsumer>
  );
};
